import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ErrorBoundary } from "@suspensive/react";
import { ArticleListSkeleton, QueryErrorFallback } from "@/shared/ui";
import { useArticleListQuery } from "../../config/useArticleListQuery";
import { SORT_TYPE, UX_EVALUATION } from "@/types/enum.ts";
import ColumnPreview from "./ColumnPreview";
import NoArticles from "./NoArticles";
import "./ArticleList.scss";
import { useLanguage } from "@/shared/hooks/useLanguageContext.tsx";
import {
  createMultilingualArticle,
  createMultilingualProduct,
} from "@/shared/utils/multilingual-helpers.ts";

interface ArticleListProps {
  selectedType: SORT_TYPE;
  onArticleClick: (id: string) => void;
  onModalOpen: (isOpen: boolean) => void;
  onResultCountChange?: (count: number) => void;
  onDataLoaded?: () => void;
  isRestoringScroll?: boolean;
}

// eslint-disable-next-line react/display-name
export const ArticleList = React.memo(
  ({
    selectedType,
    onArticleClick,
    onModalOpen,
    onResultCountChange,
    onDataLoaded,
    isRestoringScroll = false,
  }: ArticleListProps) => {
    const [searchParams] = useSearchParams();
    const articleListQuery = useArticleListQuery({
      selectedSortType: selectedType,
    });

    // 다국어 지원을 위한 훅
    const { getLocalizedText } = useLanguage();

    // 무한 스크롤을 위한 Intersection Observer
    const [loadMoreRef, setLoadMoreRef] = useState<HTMLDivElement | null>(null);

    const loadMore = useCallback(() => {
      // 스크롤 복원 중이거나 이미 로딩 중이면 무한 스크롤 비활성화
      if (
        isRestoringScroll ||
        articleListQuery.isFetchingNextPage ||
        !articleListQuery.hasNextPage
      ) {
        return;
      }
      articleListQuery.fetchNextPage();
    }, [articleListQuery, isRestoringScroll]);

    useEffect(() => {
      if (!loadMoreRef || isRestoringScroll) return;

      const observer = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting) {
            loadMore();
          }
        },
        { threshold: 0.1 },
      );

      observer.observe(loadMoreRef);
      return () => {
        observer.disconnect();
      };
    }, [loadMoreRef, loadMore, isRestoringScroll]);

    // 필터링된 아티클 목록
    const filteredArticles = useMemo(() => {
      if (!articleListQuery.data) return [];

      const allArticles = articleListQuery.data.pages.flatMap(
        (page) => page.articles,
      );

      const allKeywords = searchParams.getAll("keyword");
      const allProducts = searchParams.getAll("product");
      const allEvaluations = searchParams.getAll("uxEvaluation");

      return allArticles.filter((article) => {
        const matchesKeywords =
          allKeywords.length === 0 ||
          allKeywords.some((keyword) =>
            article.keyword.id?.includes(keyword.split("_")[1]),
          );
        const matchesProducts =
          allProducts.length === 0 ||
          allProducts.some((product) =>
            article.product?.id?.includes(product.split("_")[1]),
          );
        const matchesUxEvaluations =
          allEvaluations.length === 0 ||
          allEvaluations.some((evaluation) => {
            const paramValue =
              evaluation.split("_")[1] === "GOOD"
                ? UX_EVALUATION.GOOD
                : UX_EVALUATION.BAD;
            return article.uxEvaluation?.includes(paramValue);
          });

        return matchesKeywords && matchesProducts && matchesUxEvaluations;
      });
    }, [articleListQuery.data, searchParams]);

    // 필터가 적용되었는지 확인
    const hasActiveFilters = useMemo(() => {
      const allKeywords = searchParams.getAll("keyword");
      const allProducts = searchParams.getAll("product");
      const allEvaluations = searchParams.getAll("uxEvaluation");
      return (
        allKeywords.length > 0 ||
        allProducts.length > 0 ||
        allEvaluations.length > 0
      );
    }, [searchParams]);

    // 서버에서 이미 정렬된 아티클 목록 사용
    const sortedArticles = useMemo(() => {
      return filteredArticles; // 서버에서 이미 정렬되어 있으므로 클라이언트 사이드 정렬 불필요
    }, [filteredArticles, selectedType]);

    // 서버에서 받은 전체 개수를 부모 컴포넌트에 전달
    useEffect(() => {
      if (onResultCountChange && articleListQuery.data) {
        // 첫 번째 페이지의 total 값을 사용 (모든 페이지에서 동일)
        const totalCount = articleListQuery.data.pages[0]?.total || 0;
        onResultCountChange(totalCount);
      }
    }, [articleListQuery.data, onResultCountChange]);

    // 데이터 로딩 완료 시 부모 컴포넌트에 알림
    useEffect(() => {
      if (
        articleListQuery.data &&
        !articleListQuery.isLoading &&
        onDataLoaded
      ) {
        onDataLoaded();
      }
    }, [articleListQuery.data, articleListQuery.isLoading, onDataLoaded]);

    return (
      <div className="colum-list">
        <ErrorBoundary
          fallback={
            <QueryErrorFallback
              error={articleListQuery.error as Error}
              title="아티클 목록을 불러올 수 없습니다"
              description="아티클 데이터를 가져오는 중 오류가 발생했습니다."
            />
          }
        >
          {articleListQuery.isLoading ? (
            <>
              <ArticleListSkeleton count={6} />
            </>
          ) : (
            <>
              {sortedArticles.length !== 0 ? (
                <>
                  {sortedArticles.map((article) => {
                    // 다국어 데이터 변환
                    const multilingualTitle = createMultilingualArticle({
                      titleKo: article.titleKo,
                      titleEn: article.titleEn,
                      contentKo: article.contentKo,
                      contentEn: article.contentEn,
                    });

                    const multilingualProduct = article.product
                      ? createMultilingualProduct({
                          nameKo: article.product.nameKo,
                          nameEn: article.product.nameEn,
                        })
                      : undefined;

                    return (
                      <ColumnPreview
                        key={article.id}
                        id={article.id!}
                        title={getLocalizedText(multilingualTitle.title)}
                        content={getLocalizedText(multilingualTitle.content)}
                        uxEvaluation={article.uxEvaluation}
                        imageSrc={article.images}
                        product={{
                          name:
                            multilingualProduct &&
                            multilingualProduct.name &&
                            getLocalizedText(multilingualProduct.name),
                          imageUrl: article.product?.imageUrl,
                        }}
                        dataSource={article.dataSource}
                        author={{
                          authorName: article.author.authorName,
                          authorEmail: article.author.authorEmail,
                          authorInsta: article.author.authorInsta,
                          authorLinkedin: article.author.authorLinkedin,
                        }}
                        onClickDetail={(id) => onArticleClick(id!)}
                        setIsOpened={onModalOpen}
                      />
                    );
                  })}

                  {/* 무한 스크롤 트리거 */}
                  {articleListQuery.hasNextPage && (
                    <div ref={setLoadMoreRef} className="load-more-trigger">
                      {articleListQuery.isFetchingNextPage ? (
                        <ArticleListSkeleton count={3} />
                      ) : (
                        <div className="load-more-text">
                          더 많은 아티클 로드 중...
                        </div>
                      )}
                    </div>
                  )}
                </>
              ) : (
                <NoArticles />
              )}
            </>
          )}
        </ErrorBoundary>
      </div>
    );
  },
);
